import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { listDevices, unlinkDevice } from '../../api/telematics';
import LinkDeviceModal from '../../components/admin/LinkDeviceModal';
import SimulateCrashModal from '../../components/admin/SimulateCrashModal';

const ONLINE_THRESHOLD_MS = 15 * 60 * 1000;
const POLL_MS = 30 * 1000;

const isOnline = (d) => d.lastSeenAt && (Date.now() - new Date(d.lastSeenAt).getTime()) < ONLINE_THRESHOLD_MS;

const STATUS_STYLE = {
  provisioned: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  removed: 'bg-red-100 text-red-700',
};

function relTime(d, neverLabel) {
  if (!d) return neverLabel;
  const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function TelematicsDevices() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, presetImei: '' });
  const [simOpen, setSimOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setDevices(await listDevices(token));
    } catch (e) {
      // keep last good list; errors are transient during polling
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const onUnlink = async (d) => {
    if (!window.confirm(t('telematics.confirmUnlink'))) return;
    await unlinkDevice(d._id, token);
    load();
  };

  const total = devices.length;
  const active = devices.filter((d) => d.status === 'active').length;
  const online = devices.filter(isOnline).length;
  const totalKm = Math.round(devices.reduce((s, d) => s + (d.odometerAccumulatedKm || 0), 0));

  const Card = ({ label, value }) => (
    <div className="rounded-xl border bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-semibold text-gray-900">{value}</p>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('telematics.title')}</h1>
          <p className="text-sm text-gray-500">{t('telematics.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setSimOpen(true)}
            className="rounded-lg border border-red-300 px-4 py-2 text-sm text-red-600">
            {t('telematics.simulate.button')}
          </button>
          <button onClick={() => setModal({ open: true, presetImei: '' })}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white">
            {t('telematics.actions.link')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card label={t('telematics.summary.total')} value={total} />
        <Card label={t('telematics.summary.active')} value={active} />
        <Card label={t('telematics.summary.online')} value={online} />
        <Card label={t('telematics.summary.totalKm')} value={totalKm} />
      </div>

      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm text-gray-800">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="p-3" />
              <th className="p-3">{t('telematics.table.imei')}</th>
              <th className="p-3">{t('telematics.table.phone')}</th>
              <th className="p-3">{t('telematics.table.carrier')}</th>
              <th className="p-3">{t('telematics.table.vehicle')}</th>
              <th className="p-3">{t('telematics.table.status')}</th>
              <th className="p-3">{t('telematics.table.lastSeen')}</th>
              <th className="p-3">{t('telematics.table.odometer')}</th>
              <th className="p-3">{t('telematics.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {!loading && devices.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-gray-500">{t('telematics.empty')}</td></tr>
            )}
            {devices.map((d) => (
              <tr key={d._id} className="border-t">
                <td className="p-3">
                  <span title={isOnline(d) ? t('telematics.online') : t('telematics.offline')}
                    className={`inline-block h-2.5 w-2.5 rounded-full ${isOnline(d) ? 'bg-green-500' : 'bg-gray-300'}`} />
                </td>
                <td className="p-3 font-mono">{d.imei}</td>
                <td className="p-3">{d.simNumber || '—'}</td>
                <td className="p-3">{d.carrier || '—'}</td>
                <td className="p-3">{d.vehicle ? `${d.vehicle.plateNumber} — ${d.vehicle.brand || ''} ${d.vehicle.model || ''}` : '—'}</td>
                <td className="p-3">
                  <span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[d.status] || ''}`}>
                    {t(`telematics.status.${d.status}`)}
                  </span>
                </td>
                <td className="p-3">{relTime(d.lastSeenAt, t('telematics.never'))}</td>
                <td className="p-3">{d.vehicle?.odometerReading ?? '—'}</td>
                <td className="p-3 space-x-2 whitespace-nowrap">
                  <button onClick={() => setModal({ open: true, presetImei: d.imei })}
                    className="text-blue-600">{t('telematics.actions.reassign')}</button>
                  {d.status === 'active' && (
                    <button onClick={() => onUnlink(d)} className="text-red-600">
                      {t('telematics.actions.unlink')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LinkDeviceModal
        open={modal.open}
        presetImei={modal.presetImei}
        onClose={() => setModal({ open: false, presetImei: '' })}
        onSaved={load}
      />
      <SimulateCrashModal open={simOpen} onClose={() => setSimOpen(false)} devices={devices} />
    </div>
  );
}
